import React, { useEffect, useState } from 'react';
import {
    Grid, Typography, Divider, Tooltip, IconButton, FormControlLabel, Switch,
    TextField, Button,
    Skeleton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { ThreeDots } from 'react-loader-spinner';
import useProjects from '../../hooks/useProjects';
import VerificationUrlSection from './verification-settings.component';

const isValidUrl = (url) => {
    try {
        // Allow empty string for intermediate typing
        if (!url.trim()) return false;
        new URL(url); // throws if invalid
        return true;
    } catch (e) {
        return false;
    }
};


const VerificationSettings = ({
    isUpdatingPolicy,
    privacy,
    handleToggle,
    isUpdatingDownloadStatus,
    downlodable,
    handleToggleDownloadable,
    isSetAutoRevoke,
    revokeDurationDays,
    handleDurationChange,
    isDeletingAutoRevoke,
    handleDeleteRevokeDuration,
    isResetAutoRevoke,
    isConfigAutoRevoke,
    handleSetRevokeDuration,
    projectId,
    projectDetails

}) => {

    const {
        updateVerificationUrl,
        verificationUrl,
        updatingVerificationUrl,
        isVeirifcationUrlSaved,
        removeVerificationUrl,
        removingVerificationUrl,
        isVeirifcationUrlRemoved,
        isLoadingDetails,
    } = useProjects();

    const [tempUrl, setTempUrl] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [urlError, setUrlError] = useState(false);

    // ✅ URL validation helper
    const isValidUrl = (url) => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    // ✅ Handle Save
    const handleSave = () => {
        if (isValidUrl(tempUrl)) {
            setUrlError(false);
            const formData = new FormData();
            formData.append("verification_url", tempUrl);
            formData.append("project_id", projectId);
            updateVerificationUrl(formData);
            setIsEditing(false);
        } else {
            setUrlError(true);
        }
    };

    // ✅ Handle Edit
    const handleEdit = () => {
        setTempUrl(currentUrl || "");
        setIsEditing(true);
        setUrlError(false);
    };

    // ✅ Handle Remove
    const handleRemove = () => {
        const formData = new FormData();
        formData.append("verification_url", "");
        formData.append("project_id", projectId);
        removeVerificationUrl(formData);
        setIsEditing(false);
        setTempUrl("");
    };

    // ✅ Get the most up-to-date URL
    const currentUrl =
        verificationUrl ||
        projectDetails?.verification_url ||
        "";

    // ✅ Sync after save
    useEffect(() => {
        if (isVeirifcationUrlSaved && verificationUrl) {
            setTempUrl(verificationUrl);
            setIsEditing(false);
        }
    }, [isVeirifcationUrlSaved, verificationUrl]);

    // ✅ Sync after remove
    useEffect(() => {
        if (isVeirifcationUrlRemoved) {
            setTempUrl("");
            setIsEditing(false);
        }
    }, [isVeirifcationUrlRemoved]);

    const hasUrl = !!currentUrl;

    // ✅ Disable Save button if empty or invalid
    const disableSave =
        !tempUrl.trim() ||
        !isValidUrl(tempUrl) ||
        updatingVerificationUrl;

    return (
        <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0178f2', fontSize: 14 }}>
                    Verification Settings
                </Typography>
            </Grid>

            <Grid item xs={12} sx={{ mt: 1.5, mb: 1 }}>
                <Divider sx={{ color: 'blue' }} />
            </Grid>

            {/* Privacy Toggle */}
            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
                    Privacy
                </Typography>
                <Tooltip
                    title={
                        <Typography sx={{ fontSize: 12 }}>
                            Setting the privacy to "PRIVATE" allows the verifier to only check the owner and authenticity of the file, without accessing any information about its contents.
                        </Typography>
                    }
                    placement="top-start"
                >
                    <IconButton>
                        <InfoIcon sx={{ color: '#0178f2' }} />
                    </IconButton>
                </Tooltip>
            </Grid>

            <Grid item xs={12} sm={8} sx={{ alignSelf: "center" }}>
                {isUpdatingPolicy ? (
                    <Grid sx={{ display: "flex", alignItems: 'center' }}>
                        <ThreeDots height="25" width="25" radius="9" color="#0178f2" />
                        <Typography sx={{ ml: 1, color: "#0178f2" }}>Updating Privacy</Typography>
                    </Grid>
                ) : (
                    <FormControlLabel
                        control={<Switch checked={privacy === 'PRIVATE'} onChange={handleToggle} />}
                        label={privacy}
                    />
                )}
            </Grid>

            {/* Enable Download Toggle */}
            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
                    Enable Download
                </Typography>
                <Tooltip
                    title={
                        <Typography sx={{ fontSize: 12 }}>
                            Setting the toggle option to "No" means the verifier can still verify the authenticity of the file without downloading it.
                        </Typography>
                    }
                    placement="top-start"
                >
                    <IconButton>
                        <InfoIcon sx={{ color: '#0178f2' }} />
                    </IconButton>
                </Tooltip>
            </Grid>

            <Grid item xs={12} sm={8}>
                {privacy === "PUBLIC" ? (
                    isUpdatingDownloadStatus ? (
                        <Grid sx={{ display: "flex", alignItems: 'center' }}>
                            <ThreeDots height="25" width="25" radius="9" color="#0178f2" />
                            <Typography sx={{ ml: 1, color: "#0178f2" }}>
                                {(downlodable === "0" || downlodable === null) ? "Disabling download" : "Enabling download"}
                            </Typography>
                        </Grid>
                    ) : (
                        <FormControlLabel
                            control={<Switch checked={downlodable === '1'} onChange={handleToggleDownloadable} />}
                            label={downlodable === '0' || downlodable === null ? "NO" : "YES"}
                        />
                    )
                ) : (
                    <FormControlLabel control={<Switch disabled />} label="NO" />
                )}
            </Grid>

            {/* Auto Revoke Configuration */}
            <Grid container sx={{ ml: 2, mt: 2 }}>
                <Grid container item xs={12} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
                            Configure Auto Revoke
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={8} sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                            disabled={!isSetAutoRevoke}
                            size="small"
                            type="number"
                            inputProps={{ max: 1000, min: 1 }}
                            value={revokeDurationDays}
                            onChange={handleDurationChange}
                            sx={{ "& .MuiInputBase-root": { height: 32, width: 75 } }}
                        />
                        <Typography sx={{ ml: 1 }}>days</Typography>

                        {isSetAutoRevoke && (
                            <Button variant="text" sx={{ ml: 2, textTransform: "none" }}
                                startIcon={isDeletingAutoRevoke && (
                                    <ThreeDots height="20" width="20" radius="9" color="#0178f2" />
                                )}
                                onClick={handleDeleteRevokeDuration}
                            >
                                {isDeletingAutoRevoke ? "Resetting Revoke Duration" : "Reset"}
                            </Button>
                        )}

                        {isResetAutoRevoke && (
                            <Button variant="text" sx={{ ml: 2, textTransform: "none" }}
                                startIcon={isConfigAutoRevoke && (
                                    <ThreeDots height="20" width="20" radius="9" color="#0178f2" />
                                )}
                                onClick={handleSetRevokeDuration}
                            >
                                {isConfigAutoRevoke ? "Setting Revoke Duration" : "Save"}
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Grid>

            {/* Verification URL */}
            <VerificationUrlSection 
                projectDetails={projectDetails}
                projectId={projectId}
            />


        </Grid>
    );
};

export default VerificationSettings;
